import { useEffect, useRef, useState } from "react";

/**
 * Calls `save` after `value` has been different from `saved` for `delay` ms,
 * and again on unmount if a change is still pending.
 */
export function useDebouncedSave<T>(value: T, saved: T, save: (value: T) => void, delay = 600) {
  const saveRef = useRef(save);
  saveRef.current = save;
  const valueRef = useRef(value);
  valueRef.current = value;
  const savedRef = useRef(saved);
  savedRef.current = saved;

  useEffect(() => {
    if (Object.is(value, saved)) return;
    const id = window.setTimeout(() => {
      if (!Object.is(valueRef.current, savedRef.current)) saveRef.current(valueRef.current);
    }, delay);
    return () => window.clearTimeout(id);
  }, [value, saved, delay]);

  useEffect(() => {
    return () => {
      if (!Object.is(valueRef.current, savedRef.current)) saveRef.current(valueRef.current);
    };
  }, []);
}

/**
 * A local draft of a server field. When the server value changes to anything
 * other than what this draft last sent, the draft is replaced, so a write made
 * elsewhere (an agent, another tab) wins over unsaved local edits instead of
 * being autosaved over. Call `markSent` with the normalized value you save.
 */
export function useServerDraft(server: string, normalize: (value: string) => string = (value) => value) {
  const [draft, setDraft] = useState(server);
  const [base, setBase] = useState(server);
  const sent = useRef<string | null>(null);
  if (server !== base) {
    setBase(server);
    if (normalize(server) !== sent.current) setDraft(server);
  }
  const markSent = (value: string) => {
    sent.current = value;
  };
  return [draft, setDraft, markSent] as const;
}
